import { Component, OnInit, ViewChild } from '@angular/core';
import { Response } from '@angular/http';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable } from 'rxjs/Observable';

import { CBMError, Column, Contract, DatatableButton, Siterecord } from 'app/models';
import {
  ConfigurationStatusType,
  ContractType,
  DataProviderType,
  FileType,
  LocationOverviewType
} from 'app/models/enums';
import {
  AuthorizationService,
  ContractService,
  CsvService,
  DownloadService,
  GeojsonService,
  HttpService,
  MstService,
  MstToNcisService,
  SiterecordService,
  StorageService,
  ValidationService,
  VildChangesService,
  VildVersionService
} from 'app/services';
import { CbmTableComponent } from 'app/shared/datatable/cbm-table.component';
import { DialogContractSettingsComponent } from 'app/shared/dialog-contract-settings/dialog-contract-settings.component';
import { DialogConfirmationComponent } from 'app/shared/dialog-delete-location/dialog-confirmation.component';
import { DialogExportMstComponent } from 'app/shared/dialog-export-mst/dialogexportmst.component';
import { DialogImportMstComponent } from 'app/shared/dialog-import-mst/dialogimportmst.component';
import { DownloadExportTaskBase } from 'app/shared/export/downloadExportTaskBase';
import { MessageboxComponent } from 'app/shared/messagebox/messagebox.component';
import { IntendedUsageUtil } from 'app/utils/intended-usage.util';

@Component({
  selector: 'app-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss']
})
export class LocationsComponent extends DownloadExportTaskBase implements OnInit {
  @ViewChild(CbmTableComponent) table: CbmTableComponent<Siterecord>;
  @ViewChild(MessageboxComponent) messagebox: MessageboxComponent;
  @ViewChild('confirmDialog') confirmDialogComponent: DialogConfirmationComponent;
  @ViewChild('mstDownloadDialog') mstDownloadDialogComponent: DialogExportMstComponent;
  @ViewChild('mstToNcisDialog') mstToNcisDialogComponent: DialogExportMstComponent;
  @ViewChild(DialogImportMstComponent) importMstDialogComponent: DialogImportMstComponent;
  @ViewChild(DialogContractSettingsComponent) contractSettingsDialog: DialogContractSettingsComponent;

  public contract: Contract;
  public contractId: number;
  public locations: Siterecord[] = [];
  public locationToDelete: Siterecord;
  public showTable = true;
  public loading = false;
  public enableDownloadVildChanges = false;

  private LOCATION_MODE = 'cbm-locationMode';

  public columns: Column[] = [];

  public buttons: DatatableButton[] = [
    {
      id: 'trash',
      action: this.showConfirmDeleteDialog.bind(this),
      showIf: data => !this.isContractDataProviderTypeMst(),
      icon: 'trash',
      classes: 'btn btn-primary'
    },
    {
      id: 'pencil',
      action: this.goToLocationEditPage.bind(this),
      showIf: data => !this.isContractDataProviderTypeMst(),
      icon: 'pencil',
      classes: 'btn btn-primary'
    }
  ];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly contractService: ContractService,
    private readonly csvService: CsvService,
    private readonly geojsonService: GeojsonService,
    private readonly authorizationService: AuthorizationService,
    private readonly mstService: MstService,
    private readonly mstToNcisService: MstToNcisService,
    private readonly vildChangesService: VildChangesService,
    private readonly siterecordservice: SiterecordService,
    private readonly storageService: StorageService,
    private readonly validationService: ValidationService,
    private readonly vildVersionService: VildVersionService,
    httpService: HttpService,
    downloadService: DownloadService
  ) {
    super(httpService, downloadService);
  }

  public ngOnInit() {
    this.errorMessages = [];

    this.columns = this.initDatatableConfiguration();

    this.route.params.subscribe(params => {
      this.contractId = params['contractId'];

      this.getLocationsByContractId();
      this.getContractInformation();
    });

    this.vildVersionService.hasFutureVildVersion().subscribe(
      hasFutureVildVersion => {
        this.enableDownloadVildChanges = hasFutureVildVersion;
      },
      (errors: CBMError[]) => {
        this.errorMessages = errors;
      }
    );
  }

  public isContractTypeDataProviderOrFcdAndLocationAdmin(): boolean {
    return this.isContractTypeDataProvider() || (this.isContractTypeFcd && this.authorizationService.isLocationAdmin());
  }

  public isContractTypeDataProvider(): boolean {
    if (this.contract) {
      return this.contract.contractType === ContractType.DataProvider;
    }
    return false;
  }

  public isContractTypeFcd(): boolean {
    if (this.contract) {
      return this.contract.contractType === ContractType.FCD;
    }
    return false;
  }

  public isContractDataProviderTypeMst(): boolean {
    if (this.contract) {
      return this.contract.dataproviderType === DataProviderType.MST;
    }
    return false;
  }

  public isContractDataProviderTypeIntekenenOrTypeFcd() {
    return this.isContractDataProviderTypeIntekenen() || this.isContractTypeFcd();
  }

  public isContractDataProviderTypeIntekenen(): boolean {
    if (this.contract) {
      return this.contract.dataproviderType === DataProviderType.Intekenen;
    }
    return false;
  }

  public allowedToImportMst(): boolean {
    return (
      this.contract &&
      this.isContractTypeDataProvider() &&
      (this.isContractDataProviderTypeMst() ||
        (this.locations && this.locations.length === 0) ||
        this.locations === null)
    );
  }

  public goToLocationdetailPage(location: Siterecord) {
    this.storageService.setLocationListInStorage(this.table.getListOfPropertiesOfDisplayedData(['id', 'locationType']));
    this.storageService.setLocationOverviewOrigin(LocationOverviewType.LOCATION_OVERVIEW_USER);
    this.router.navigate([`/location/${location.locationType}/detail/${location.id}`]);
  }

  public goToLocationEditPage(location: Siterecord) {
    this.storageService.setLocationListInStorage(this.table.getListOfPropertiesOfDisplayedData(['id', 'locationType']));
    this.storageService.setLocationOverviewOrigin(LocationOverviewType.LOCATION_OVERVIEW_USER);
    this.router.navigate([`/location/${location.locationType}/edit/${location.id}`]);
  }

  public newMeasurementBox(): void {
    this.router.navigate([`/location/${this.contractId}/linear/new`]);
  }

  public newMeasurementPoint(): void {
    this.router.navigate([`/location/${this.contractId}/point/new`]);
  }

  public backToContracts(): void {
    this.router.navigate(['/contracts']);
  }

  public showConfirmDeleteDialog(location: Siterecord): void {
    this.errorMessages = [];
    this.locationToDelete = location;
    this.confirmDialogComponent.show();
  }

  public showDownloadMstDialog(): void {
    this.errorMessages = [];
    this.mstDownloadDialogComponent.show();
  }

  public showMstToNcisDialog(): void {
    this.errorMessages = [];
    this.mstToNcisDialogComponent.show();
  }

  public totalAmountOfLocations(): number {
    return this.locations.length;
  }

  public amountOfUnValidatedLocations(): number {
    const errorStatuses = [
      ConfigurationStatusType.AUTO_VALIDATED_CORRECT,
      ConfigurationStatusType.AUTO_VALIDATED_ERROR,
      ConfigurationStatusType.ERROR
    ];
    return this.locations.filter(sr => errorStatuses.indexOf(sr.status) !== -1).length;
  }

  public amountOfCorrectAndInactiveLocations(): number {
    return this.locations.filter(sr => {
      if (sr.status === ConfigurationStatusType.CORRECT) {
        return sr.active === false;
      }
      return false;
    }).length;
  }

  public showImportMstDialog(): void {
    this.importMstDialogComponent.show();
  }

  public deleteLocation(location: Siterecord): void {
    this.siterecordservice.deleteSiterecord(this.locationToDelete.id).subscribe(response => {
      this.confirmDialogComponent.hide();
      this.getLocationsByContractId();
    });
  }

  public clickShowTable(): void {
    this.showTable = true;
    localStorage.setItem(this.LOCATION_MODE, 'table');
  }

  public clickShowMap(): void {
    this.showTable = false;
    localStorage.setItem(this.LOCATION_MODE, 'map');
  }

  private getLocationsByContractId(): void {
    this.loading = true;
    this.siterecordservice.getSiterecordsByContractId(this.contractId).subscribe(locations => {
      this.locations = locations ? locations : [];
      if (localStorage.getItem(this.LOCATION_MODE) === 'map') {
        this.showTable = false;
      }
      this.loading = false;
    });
  }

  private getContractInformation(): void {
    this.contractService.getContract(this.contractId).subscribe(contract => {
      this.siterecordservice.contract = contract;
      this.contract = contract;
      this.buttons = [...this.buttons];
      this.columns = this.initDatatableConfiguration();
    });
  }

  public showContractSettingsDialog(): void {
    this.contractSettingsDialog.show();
  }

  public downloadFile(fileType: FileType) {
    this.setDownloading(true);
    this.errorMessages = [];

    let downloadFile: Observable<Response>;
    switch (fileType) {
      case FileType.csv:
        downloadFile = this.csvService.downloadCsvContract(this.contractId);
        break;
      case FileType.geojsonlinear:
        downloadFile = this.geojsonService.downloadGeojsonContract(this.contractId, 'linear');
        break;
      case FileType.geojsonpoint:
        downloadFile = this.geojsonService.downloadGeojsonContract(this.contractId, 'point');
        break;
      case FileType.validations:
        downloadFile = this.validationService.downloadValidations([this.contractId]);
        break;
      case FileType.vildchanges:
        downloadFile = this.vildChangesService.downloadVildChangesContracts([this.contractId]);
        break;
    }
    this.handleTaskAsync(downloadFile);
  }

  public downloadMstFile(soapEnvelope: boolean) {
    this.setDownloading(true);
    this.errorMessages = [];

    const downloadFile = this.mstService.downloadMst(this.contractId, soapEnvelope);
    this.handleTaskAsync(downloadFile);
  }

  public mstToNcis(): void {
    this.errorMessages = [];
    this.mstToNcisService.sendMstToNcis(this.contractId).subscribe(
      (res: Response) => console.log(res),
      (errors: CBMError[]) => {
        this.errorMessages = errors;
      }
    );
  }

  public parseIntendedUsageType(location: Siterecord): string {
    return IntendedUsageUtil.parseIntendedUsageTypeAsString(location);
  }

  private initDatatableConfiguration(): Column[] {
    const columns = this.initColumns();
    this.appendContractTypeColumns(columns);
    return columns;
  }

  private initColumns(): Column[] {
    if (this.authorizationService.isLocationAdmin()) {
      return this.initAdminColumns();
    } else {
      return this.initUserColumns();
    }
  }

  private initUserColumns(): Column[] {
    return [
      { property: 'id', label: 'LOCATIONS.ID', type: 'string', filterable: true },
      { property: 'recordId', label: 'LOCATIONS.RECORDID', type: 'string', filterable: true },
      { property: 'recordName', label: 'LOCATIONS.RECORDNAME', type: 'string', filterable: true },
      { property: 'recordVersionNr', label: 'LOCATIONS.RECORDVERSIONNUMBER', type: 'string', filterable: true },
      { property: 'recordVersionDateTime', label: 'LOCATIONS.RECORDVERSIONDATETIME', type: 'date' },
      // locationType also used in navigation with getListOfPropertiesOfDisplayedData
      { property: 'locationType', label: 'LOCATIONS.LOCATIONTYPE', type: 'string', filterable: true },
      { property: 'status', label: 'LOCATIONS.STATUS', type: 'string', filterable: true },
      { property: 'active', label: 'LOCATIONS.ACTIVE', type: 'string', filterable: true }
    ];
  }

  private initAdminColumns(): Column[] {
    return [
      { property: 'id', label: 'LOCATIONS.ID', type: 'string', filterable: true, flexGrow: 1.5 },
      { property: 'recordId', label: 'LOCATIONS.RECORDID', type: 'string', filterable: true },
      { property: 'recordName', label: 'LOCATIONS.RECORDNAME', type: 'string', filterable: true },
      {
        property: 'recordVersionNr',
        label: 'LOCATIONS.RECORDVERSIONNUMBER',
        type: 'string',
        filterable: true,
        flexGrow: 1
      },
      { property: 'recordVersionDateTime', label: 'LOCATIONS.RECORDVERSIONDATETIME', type: 'date' },
      // locationType also used in navigation with getListOfPropertiesOfDisplayedData
      { property: 'locationType', label: 'LOCATIONS.LOCATIONTYPE', type: 'string', filterable: true },
      { property: 'status', label: 'LOCATIONS.STATUS', type: 'string', filterable: true, flexGrow: 1.5 },
      { property: 'fcdEncodingStatus', label: 'LOCATIONS.FCDENCODINGSTATUS', type: 'string', filterable: true },
      { property: 'vildEncodingStatus', label: 'LOCATIONS.VILDENCODINGSTATUS', type: 'string', filterable: true },
      { property: 'active', label: 'LOCATIONS.ACTIVE', type: 'string', filterable: true, flexGrow: 1.5 },
      { property: 'inShapefile', label: 'LOCATIONS.INSHAPEFILE', type: 'string', filterable: true, flexGrow: 1.5 },
      { property: 'mutationDateTime', label: 'LOCATIONS.MUTATIONDATETIME', type: 'date', flexGrow: 2.2 }
    ];
  }

  private appendContractTypeColumns(columns: Column[]) {
    if (this.isContractTypeDataProvider() && !this.authorizationService.isLocationAdmin()) {
      // vildEncodingStatus is a default column for admins
      columns.splice(7, 0, {
        property: 'vildEncodingStatus',
        label: 'LOCATIONS.VILDENCODINGSTATUS',
        type: 'string',
        filterable: true
      });
    } else if (this.isContractTypeFcd()) {
      columns.push({
        property: 'intendedUsageType',
        label: 'LOCATIONS.INTENDEDUSAGE',
        type: 'translatable',
        valueCallback: this.parseIntendedUsageType.bind(this),
        filterable: true,
        flexGrow: 2
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/locations/locations.component.ts